<template>
  <div v-if="visible" class="fixed inset-0 flex items-center justify-center z-70 p-4 bg-black/40 backdrop-blur-sm" @click.self="closeModal">
    <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div class="flex items-center gap-3">
          <!-- Icono según modo -->
          <div :class="[
            'w-8 h-8 rounded-full flex items-center justify-center',
            isEditMode ? 'bg-yellow-100 text-yellow-600' :
              isDeleteMode ? 'bg-red-100 text-red-600' :
                'bg-blue-100 text-blue-600'
          ]">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!isEditMode && !isDeleteMode" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              <path v-else-if="isEditMode" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
            <p class="text-sm text-gray-500">{{ modalSubtitle }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- Botón editar (solo en modo vista) -->
          <button v-if="canEdit && !isEditMode && !isDeleteMode" @click="startEdit" :disabled="processing" class="w-8 h-8 rounded-full bg-gray-100 hover:bg-yellow-100 text-gray-600 hover:text-yellow-600 flex items-center justify-center transition-colors disabled:opacity-50" title="Editar dispositivo">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <!-- Botón eliminar (solo en modo vista) -->
          <button v-if="canDelete && !isDeleteMode" @click="startDelete" :disabled="processing" class="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center transition-colors disabled:opacity-50" title="Eliminar dispositivo">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <!-- Botón cerrar -->
          <button @click="closeModal" :disabled="processing" class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors disabled:opacity-50">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Contenido -->
      <div class="p-6">
        <!-- Modo eliminación -->
        <div v-if="isDeleteMode" class="space-y-5">
          <div class="bg-gray-50 border border-red-200 rounded-lg p-4">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 class="font-medium text-gray-900 mb-2">Confirmar eliminación</h4>
                <p class="text-sm text-gray-600 mb-3">
                  Esta acción eliminará permanentemente el dispositivo y no se puede deshacer.
                </p>
                <div class="bg-white rounded border border-gray-200 p-3 text-sm space-y-1">
                  <div class="flex justify-between">
                    <span class="text-gray-500">Nombre:</span>
                    <span class="font-medium text-gray-900">{{ dispositivoForm.nombre_dispositivo }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Ubicación:</span>
                    <span class="font-medium text-gray-900">{{ dispositivoForm.ubicacion_dispositivo || 'Sin definir' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Modelo:</span>
                    <span class="font-medium text-gray-900">{{ dispositivoForm.modelo_dispositivo || 'IC270' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Fabricante:</span>
                    <span class="font-medium text-gray-900">{{ dispositivoForm.fabricante || 'Sin definir' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Para confirmar, escribe "<strong class="font-semibold">{{ dispositivoForm.nombre_dispositivo }}</strong>"
            </label>
            <input v-model="deleteConfirmationText" type="text" :placeholder="`Escribe ${dispositivoForm.nombre_dispositivo}`" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors" :class="{ 'border-green-500 bg-green-50': isDeleteConfirmationValid }" />
          </div>
        </div>

        <!-- Formulario creación/edición -->
        <form v-else @submit.prevent="submitForm" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Nombre (ocupa 2 columnas) -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nombre del Dispositivo *</label>
              <input v-model="dispositivoForm.nombre_dispositivo" type="text" required :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50" placeholder="Ej: HVAC Principal Piso 1" maxlength="100" />
              <p class="text-xs text-gray-500 mt-1">Máximo 100 caracteres.</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
              <input v-model="dispositivoForm.modelo_dispositivo" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Ej: IC270" maxlength="50" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fabricante</label>
              <input v-model="dispositivoForm.fabricante" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Ej: Mitsubishi" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
              <input v-model="dispositivoForm.ubicacion_dispositivo" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Ej: Planta Baja" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Equipo</label>
              <select v-model="dispositivoForm.tipo_dispositivo" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white">
                <option value="1">HVAC</option>
                <option value="2">Split</option>
                <option value="3">VRF</option>
                <option value="4">Extractor</option>
                <option value="5">Otro</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Potencia Nominal (W)</label>
              <input v-model.number="dispositivoForm.potencia_nominal" type="number" min="0" step="1" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Ej: 1200" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fecha Última Mantención</label>
              <input v-model="dispositivoForm.fecha_ultima_mantencion" type="date" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" />
            </div>
          </div>

          <!-- Configuración MQTT -->
          <div class="border border-gray-200 rounded-lg p-5 bg-gray-50/50">
            <h4 class="text-sm font-semibold text-gray-800 mb-4">Configuración MQTT</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Broker URL</label>
                <input v-model="dispositivoForm.brokerConfig.url" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="mqtt.ejemplo.com" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Puerto</label>
                <input v-model.number="dispositivoForm.brokerConfig.puerto" type="number" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="1883" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                <input v-model="dispositivoForm.brokerConfig.usuario" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="usuario_mqtt" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                <input v-model="dispositivoForm.brokerConfig.contrasena" type="password" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="••••••••" />
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                <input v-model="dispositivoForm.brokerConfig.client_id" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="ID_unico_dispositivo" />
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Tópico</label>
                <input v-model="dispositivoForm.brokerConfig.topico" type="text" :disabled="processing" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="dispositivos/local_1/hvac_1" />
              </div>

              <div class="md:col-span-2 flex items-center gap-2">
                <input id="usa_tls" v-model="dispositivoForm.brokerConfig.usa_tls" type="checkbox" :disabled="processing" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label for="usa_tls" class="text-sm font-medium text-gray-700">Usar TLS</label>
              </div>

              <template v-if="dispositivoForm.brokerConfig.usa_tls">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Certificado CA (ca.cer)</label>
                  <textarea v-model="dispositivoForm.brokerConfig.ca_cer" :disabled="processing" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-xs" placeholder="-----BEGIN CERTIFICATE----- ..."></textarea>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Certificado Cliente (client.cer)</label>
                  <textarea v-model="dispositivoForm.brokerConfig.client_cer" :disabled="processing" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-xs" placeholder="-----BEGIN CERTIFICATE----- ..."></textarea>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Clave Cliente (client.key)</label>
                  <textarea v-model="dispositivoForm.brokerConfig.client_key" :disabled="processing" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-xs" placeholder="-----BEGIN PRIVATE KEY----- ..."></textarea>
                </div>
              </template>
            </div>
          </div>

          <!-- Imagen del dispositivo -->
          <div class="border border-gray-200 rounded-lg p-5">
            <h4 class="text-sm font-semibold text-gray-800 mb-4">Imagen del Dispositivo</h4>
            <div class="flex flex-col sm:flex-row gap-5">
              <div class="flex-shrink-0">
                <div class="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  <img v-if="imagePreview" :src="imagePreview" alt="Preview" class="w-full h-full object-cover" @error="handleImageError" />
                  <div v-else class="text-center p-2">
                    <svg class="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p class="text-xs text-gray-500 mt-1">Preview</p>
                  </div>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ isEditMode ? 'Cambiar imagen (opcional)' : 'Seleccionar imagen (opcional)' }}
                </label>
                <div class="relative">
                  <input ref="fileInput" type="file" accept="image/*" @change="handleMediaUpload" :disabled="processing" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                  <div class="border border-gray-300 rounded-lg px-4 py-3 bg-white hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span class="text-sm text-gray-600 truncate">
                      {{ selectedMediaFile ? selectedMediaFile.name : (isEditMode && currentImageUrl ? 'Hacer clic para cambiar' : 'Seleccionar archivo') }}
                    </span>
                  </div>
                </div>
                <p class="text-xs text-gray-500 mt-1">JPG, PNG, GIF · Max 5MB</p>
                <div class="flex flex-wrap gap-3 mt-2">
                  <button v-if="selectedMediaFile" type="button" @click="clearSelectedFile" :disabled="processing" class="text-xs text-red-600 hover:text-red-800 flex items-center gap-1">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Quitar
                  </button>
                  <button v-if="isEditMode && currentImageUrl && !imageRemoved" type="button" @click="removeImage" :disabled="processing" class="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Eliminar actual
                  </button>
                  <span v-if="imageRemoved" class="text-xs text-gray-500 italic">Imagen actual será eliminada</span>
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- Footer con botones -->
        <div class="flex items-center justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
          <button type="button" @click="closeModal" :disabled="processing" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50">
            Cancelar
          </button>
          <button v-if="isDeleteMode" @click="deleteDispositivo" :disabled="processing || !isDeleteConfirmationValid" class="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 flex items-center gap-2">
            <svg v-if="processing" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>{{ processing ? 'Eliminando...' : 'Eliminar Dispositivo' }}</span>
          </button>
          <button v-else @click="submitForm" :disabled="processing || !isFormValid" :class="[
            'px-5 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center gap-2',
            isEditMode ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
          ]">
            <svg v-if="processing" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>{{ processing ? (isEditMode ? 'Actualizando...' : 'Creando...') : (isEditMode ? 'Actualizar' : 'Crear') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DispositivosService from '@/services/dispositivos/dispositivos.js'

export default {
  name: 'ModalGestionDispositivos',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    local: {
      type: Object,
      required: true
    },
    dispositivo: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create', // 'create', 'edit', 'delete'
      validator: (value) => ['create', 'edit', 'delete'].includes(value)
    }
  },

  emits: [
    'close',
    'mode-change',
    'dispositivo-created',
    'dispositivo-updated',
    'dispositivo-deleted'
  ],

  data() {
    return {
      processing: false,
      deleteConfirmationText: '',

      // Estados para la subida de imagen (IGUAL que LocalesView)
      selectedMediaFile: null,
      imagePreview: null,
      currentImageUrl: null,
      imageRemoved: false,

      // Formulario del dispositivo
      dispositivoForm: {
        nombre_dispositivo: '',
        modelo_dispositivo: '',
        fabricante: '',
        ubicacion_dispositivo: '',
        tipo_dispositivo: '1',
        potencia_nominal: 0,
        fecha_ultima_mantencion: '',
        imagen: 'ImagenDefault',
        media: '',
        configuracion_dispositivo: {
          Modo: 2,
          Occupied: false,
          Setpoint: 0,
          'Temp Retorno': 0,
          'Temp Inyeccion': 0
        },
        brokerConfig: {
          url: '',
          puerto: 1883,
          usuario: '',
          contrasena: '',
          client_id: '',
          topico: '',
          usa_tls: false,
          ca_cer: '',
          client_cer: '',
          client_key: ''
        }
      }
    }
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit'
    },

    isDeleteMode() {
      return this.mode === 'delete'
    },

    canEdit() {
      return this.dispositivo && this.mode !== 'create'
    },

    canDelete() {
      return this.dispositivo && this.mode !== 'create'
    },

    modalTitle() {
      switch (this.mode) {
        case 'edit':
          return 'Editar Dispositivo'
        case 'delete':
          return 'Eliminar Dispositivo'
        default:
          return 'Crear Nuevo Dispositivo'
      }
    },

    modalSubtitle() {
      const localInfo = `Local N° ${this.local?.numero_local || 'N/A'}`
      if (this.dispositivo) {
        return `${localInfo} - ${this.dispositivo.nombre_dispositivo}`
      }
      return localInfo
    },

    isFormValid() {
      return this.dispositivoForm.nombre_dispositivo.trim().length > 0
    },

    isDeleteConfirmationValid() {
      return this.deleteConfirmationText.trim() === this.dispositivoForm.nombre_dispositivo
    }
  },

  watch: {
    visible: {
      handler(isVisible) {
        // Bloquear/desbloquear scroll del body
        document.body.style.overflow = isVisible ? 'hidden' : ''

        if (isVisible) {
          this.initializeForm()
        } else {
          this.resetForm()
        }
      },
      immediate: true
    },

    dispositivo: {
      handler() {
        if (this.visible) {
          this.initializeForm()
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    // Restaurar scroll del body
    document.body.style.overflow = ''
  },
  methods: {
    // ---------------------------------------------------
    // Gestión del Modal y Formulario
    // ---------------------------------------------------
    // Inicializa formulario según modo con manejo de imágenes
    initializeForm() {
      this.selectedMediaFile = null
      this.imagePreview = null
      this.currentImageUrl = null
      this.imageRemoved = false

      if (this.isEditMode || this.isDeleteMode) {
        if (this.dispositivo) {
          this.dispositivoForm = {
            nombre_dispositivo: this.dispositivo.nombre_dispositivo || '',
            modelo_dispositivo: this.dispositivo.modelo_dispositivo || '',
            ubicacion_dispositivo: this.dispositivo.ubicacion_dispositivo || '',
            fabricante: this.dispositivo.fabricante || '',
            tipo_dispositivo: this.dispositivo.tipo_dispositivo?.toString() || '1',
            potencia_nominal: this.dispositivo.potencia_nominal || 0,
            fecha_ultima_mantencion: this.dispositivo.fecha_ultima_mantencion || '',
            imagen: this.dispositivo.imagen || 'ImagenDefault',
            media: this.dispositivo.media || '',
            configuracion_dispositivo: this.dispositivo.configuracion_dispositivo || this.getDefaultConfig(),
            brokerConfig: {
              ...this.getBrokerDefaults(),
              ...(this.dispositivo.broker_config || {})
            }
          }
          if (this.dispositivo.media) {
            this.currentImageUrl = this.getImageUrl(this.dispositivo.media)
            this.imagePreview = this.currentImageUrl
          }
        }
      } else {
        this.dispositivoForm = {
          nombre_dispositivo: '',
          modelo_dispositivo: '',
          ubicacion_dispositivo: '',
          fabricante: '',
          tipo_dispositivo: '1',
          potencia_nominal: 0,
          fecha_ultima_mantencion: '',
          imagen: 'ImagenDefault',
          media: '',
          configuracion_dispositivo: this.getDefaultConfig(),
          brokerConfig: this.getBrokerDefaults()
        }
      }
      this.deleteConfirmationText = ''
    },
    // Resetea formulario completamente
    resetForm() {
      this.dispositivoForm = {
        nombre_dispositivo: '',
        modelo_dispositivo: '',
        ubicacion_dispositivo: '',
        fabricante: '',
        tipo_dispositivo: '1',
        potencia_nominal: 0,
        fecha_ultima_mantencion: '',
        imagen: 'ImagenDefault',
        media: '',
        configuracion_dispositivo: this.getDefaultConfig(),
        brokerConfig: this.getBrokerDefaults()
      }
      this.deleteConfirmationText = ''
      this.processing = false
      this.clearSelectedFile()
      this.currentImageUrl = null
      this.imageRemoved = false
    },
    // Cierra el modal si no hay procesamiento en curso
    closeModal() {
      if (!this.processing) {
        this.$emit('close')
      }
    },
    // ---------------------------------------------------
    // Cambio de Modo del Modal
    // ---------------------------------------------------
    // Cambia a modo edición
    startEdit() {
      this.$emit('mode-change', 'edit')
    },
    // Cambia a modo eliminación
    startDelete() {
      this.$emit('mode-change', 'delete')
    },
    // ---------------------------------------------------
    // Gestión de Imágenes
    // ---------------------------------------------------
    // Maneja selección de archivo de imagen con validaciones
    handleMediaUpload(event) {
      const file = event.target.files[0]
      if (!file) {
        this.clearSelectedFile()
        return
      }
      try {
        DispositivosService.validarArchivoImagen(file)
        this.selectedMediaFile = file
        this.createImagePreview(file)
        this.imageRemoved = false
      } catch (error) {
        this.showError(error.message)
        event.target.value = ''
        this.clearSelectedFile()
      }
    },
    // Crea vista previa de imagen seleccionada
    createImagePreview(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    // Limpia archivo seleccionado y vista previa
    clearSelectedFile() {
      this.selectedMediaFile = null
      this.imagePreview = null
      const fileInput = document.getElementById('media-upload')
      if (fileInput) {
        fileInput.value = ''
      }
    },
    // Elimina imagen actual en modo edición
    removeImage() {
      this.selectedMediaFile = null
      this.imagePreview = null
      this.dispositivoForm.media = ''
      if (this.isEditMode) {
        this.imageRemoved = true
        this.currentImageUrl = null
      }
      const fileInput = document.getElementById('media-upload')
      if (fileInput) {
        fileInput.value = ''
      }
    },
    // Obtiene URL completa de imagen usando servicio
    getImageUrl(mediaPath) {
      return DispositivosService.obtenerUrlImagen(mediaPath)
    },
    // Maneja errores de carga de imagen
    handleImageError(event) {
      event.target.style.display = 'none'
    },
    // ---------------------------------------------------
    // CRUD de Dispositivos
    // ---------------------------------------------------
    // Envía formulario según modo actual (crear o editar)
    async submitForm() {
      if (this.isEditMode) {
        await this.updateDispositivo()
      } else {
        await this.createDispositivo()
      }
    },
    // Crea nuevo dispositivo con una sola llamada al servicio
    async createDispositivo() {
      if (!this.isFormValid) {
        this.showError('Por favor completa el nombre del dispositivo')
        return
      }
      this.processing = true
      try {
        const datosDispositivo = {
          nombre_dispositivo: this.dispositivoForm.nombre_dispositivo,
          modelo_dispositivo: this.dispositivoForm.modelo_dispositivo,
          ubicacion_dispositivo: this.dispositivoForm.ubicacion_dispositivo,
          fabricante: this.dispositivoForm.fabricante,
          tipo_dispositivo: parseInt(this.dispositivoForm.tipo_dispositivo),
          potencia_nominal: this.dispositivoForm.potencia_nominal || 0,
          fecha_ultima_mantencion: this.dispositivoForm.fecha_ultima_mantencion,
          imagen: this.dispositivoForm.imagen || 'ImagenDefault',
          activo_dispositivo: true,
          local_id: this.local.id,
          id_empresa: this.local.id_empresa || this.local.empresa_id,
          configuracion_dispositivo: this.dispositivoForm.configuracion_dispositivo,
          brokerConfig: this.dispositivoForm.brokerConfig
        }
        const dispositivoCreado = await DispositivosService.crearDispositivo(
          datosDispositivo,
          this.selectedMediaFile
        )
        this.$emit('dispositivo-created', dispositivoCreado)
        this.closeModal()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.processing = false
      }
    },
    // Actualiza dispositivo existente con una sola llamada al servicio
    async updateDispositivo() {
      if (!this.isFormValid) {
        this.showError('Por favor completa el nombre del dispositivo')
        return
      }
      this.processing = true
      try {
        const datosDispositivo = {
          nombre_dispositivo: this.dispositivoForm.nombre_dispositivo,
          modelo_dispositivo: this.dispositivoForm.modelo_dispositivo,
          ubicacion_dispositivo: this.dispositivoForm.ubicacion_dispositivo,
          fabricante: this.dispositivoForm.fabricante,
          tipo_dispositivo: parseInt(this.dispositivoForm.tipo_dispositivo),
          potencia_nominal: this.dispositivoForm.potencia_nominal || 0,
          fecha_ultima_mantencion: this.dispositivoForm.fecha_ultima_mantencion,
          imagen: this.dispositivoForm.imagen,
          activo_dispositivo: true,
          local_id: this.local.id,
          id_empresa: this.local.id_empresa || this.local.empresa_id,
          configuracion_dispositivo: this.dispositivoForm.configuracion_dispositivo,
          brokerConfig: this.dispositivoForm.brokerConfig
        }
        const dispositivoActualizado = await DispositivosService.actualizarDispositivo(
          this.dispositivo.id,
          datosDispositivo,
          this.selectedMediaFile,
          this.imageRemoved
        )
        this.$emit('dispositivo-updated', dispositivoActualizado)
        this.showSuccess(`Dispositivo "${dispositivoActualizado.nombre_dispositivo}" actualizado exitosamente`)
        this.closeModal()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.processing = false
      }
    },
    // Elimina dispositivo con confirmación
    async deleteDispositivo() {
      if (!this.isDeleteConfirmationValid) {
        this.showError('Por favor confirma escribiendo el nombre del dispositivo correctamente')
        return
      }
      this.processing = true
      try {
        this.$emit('dispositivo-deleted', this.dispositivo)
        this.showSuccess(`Dispositivo "${this.dispositivoForm.nombre_dispositivo}" eliminado exitosamente`)
        this.closeModal()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.processing = false
      }
    },
    // ---------------------------------------------------
    // Utilidades y Configuraciones
    // ---------------------------------------------------
    // Obtiene configuración por defecto del dispositivo
    getDefaultConfig() {
      return {
        Modo: 2,
        Occupied: false,
        Setpoint: 0,
        'Temp Retorno': 0,
        'Temp Inyeccion': 0
      }
    },
    // Obtiene configuración de broker por defecto
    getBrokerDefaults() {
      return {
        url: '',
        puerto: 1883,
        usuario: '',
        contrasena: '',
        client_id: '',
        topico: '',
        usa_tls: false,
        ca_cer: '',
        client_cer: '',
        client_key: ''
      }
    },
    // Muestra mensaje de éxito al usuario
    showSuccess(message) {
      alert(message)
    },
    // Muestra mensaje de error al usuario
    showError(message) {
      alert(message)
    }
  }
}
</script>

<style scoped>
/* Transiciones para el modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-content-enter-active,
.modal-content-leave-active {
  transition: transform 0.3s ease;
}

.modal-content-enter-from,
.modal-content-leave-to {
  transform: scale(0.9) translateY(-10px);
}

/* Estilos responsivos */
@media (max-width: 640px) {
  .modal-container {
    padding: 1rem;
  }

  .modal-content {
    margin: 0;
    border-radius: 0.5rem;
  }
}

/* Animación del spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>